import { listDocumentsForOwners } from "../db/documents";
import { ensureJourneyDb, getBlob, listTable } from "../db";
import { getAccessibleTransactions } from "./messaging/scope";

function isImage(item) {
  return String(item.mime_type || item.kind || "").startsWith("image/") || item.kind === "photo";
}

function sellerPhotos(sellers = []) {
  return sellers.flatMap((seller) =>
    (seller.portfolio?.photos?.items || []).map((item) => ({
      id: item.id,
      name: item.fileName || item.caption || "Seller photo",
      type: item.mime_type || "image/*",
      kind: "photo",
      source: "seller-checklist",
      ownerId: seller.id,
      ownerName: seller.name || "",
      stamp: item.addedAt || "",
      file_path: item.file_path || "",
      caption: [item.room, item.caption].filter(Boolean).join(" · "),
    }))
  );
}

function buyerPhotos(buyers = []) {
  return buyers.flatMap((buyer) =>
    (buyer.preferences?.notes?.attachments || []).map((item) => ({
      id: item.id,
      name: item.file_name || item.fileName || "Buyer photo",
      type: item.mime_type || "",
      kind: item.kind || "document",
      source: "buyer-checklist",
      ownerId: buyer.id,
      ownerName: buyer.name || "",
      stamp: item.created_at || item.addedAt || "",
      file_path: item.file_path || "",
    }))
  );
}

function messageFiles(transactions = []) {
  return transactions.flatMap((transaction) =>
    (transaction.threads || []).flatMap((thread) =>
      (thread.messages || []).flatMap((message) =>
        (message.attachments || []).map((file) => ({
          id: file.id,
          name: file.file_name || "Attachment",
          type: file.mime_type || "",
          kind: String(file.mime_type || "").startsWith("image/") ? "photo" : "document",
          source: "message",
          ownerId: transaction.client_id,
          ownerName: transaction.client?.name || "",
          stamp: file.uploaded_at || message.timestamp || "",
          file_path: file.file_path || "",
          transactionId: transaction.id,
        }))
      )
    )
  );
}

function overlayFiles() {
  try {
    const map = JSON.parse(localStorage.getItem("dh_file_packets") || "{}");
    return Object.values(map).flatMap((overlay) => {
      const office = (overlay.attachments || []).map((file) => ({
        id: file.id,
        name: file.file_name || file.name || "File attachment",
        type: file.mime_type || "",
        kind: String(file.mime_type || "").startsWith("image/") ? "photo" : "document",
        source: "file-packet",
        ownerId: overlay.id,
        ownerName: "",
        stamp: file.added_at || overlay.updatedAt || "",
        file_path: file.file_path || "",
        fileId: overlay.id,
      }));
      const custom = (overlay.customSteps || []).flatMap((step) =>
        (step.attachments || []).map((file) => ({
          id: file.id,
          name: file.file_name || file.name || "Custom-step file",
          type: file.mime_type || "",
          kind: String(file.mime_type || "").startsWith("image/") ? "photo" : "document",
          source: "file-packet",
          ownerId: overlay.id,
          ownerName: step.title || "",
          stamp: file.added_at || step.date || "",
          file_path: file.file_path || "",
          fileId: overlay.id,
        }))
      );
      return [...office, ...custom];
    });
  } catch {
    return [];
  }
}

export function listDeskReports(session) {
  ensureJourneyDb();
  const transactions = getAccessibleTransactions(session);
  const allowed = new Set(transactions.map((item) => item.id));
  return listTable("exports")
    .filter((row) => allowed.has(row.transaction_id))
    .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))
    .map((row) => {
      const transaction = transactions.find((item) => item.id === row.transaction_id);
      return {
        id: row.id,
        name: `Communication report · ${transaction?.property_address || row.transaction_id}`,
        type: "text/html",
        kind: "report",
        source: "report",
        ownerId: row.generated_by_user_id,
        ownerName: transaction?.client?.name || "",
        stamp: row.created_at,
        file_path: row.file_path,
        transactionId: row.transaction_id,
        exportType: row.export_type,
      };
    });
}

export function collectDeskMedia({ session, buyers = [], sellers = [] }) {
  const transactions = getAccessibleTransactions(session);
  const ownerIds = [
    session?.userId,
    ...buyers.map((item) => item.id),
    ...sellers.map((item) => item.id),
    ...transactions.map((item) => item.client_id),
  ];
  const docs = listDocumentsForOwners(ownerIds).map((row) => ({
    id: row.id,
    name: row.file_name,
    type: row.mime_type,
    kind: row.kind || (String(row.mime_type || "").startsWith("image/") ? "photo" : "document"),
    source: row.kind === "photo" ? `${row.role}-checklist` : "document",
    ownerId: row.owner_id,
    ownerName:
      buyers.find((item) => item.id === row.owner_id)?.name ||
      sellers.find((item) => item.id === row.owner_id)?.name ||
      "",
    stamp: row.created_at,
    file_path: row.file_path,
  }));

  const items = [
    ...docs,
    ...messageFiles(transactions),
    ...overlayFiles(),
    ...sellerPhotos(sellers),
    ...buyerPhotos(buyers),
    ...listDeskReports(session),
  ];

  const seen = new Set();
  return items.filter((item) => {
    const key = item.file_path || item.id;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function mediaHref(item) {
  if (!item?.file_path) return "";
  const blob = getBlob(item.file_path);
  return typeof blob === "string" ? blob : "";
}

export function isMediaImage(item) {
  return isImage(item);
}
